import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import {
  PROFILE_SEARCH_NORMALIZE_AMPLITUDE,
  PROFILE_SEARCH_NORMALIZE_LOCATION,
  PROFILE_SEARCH_SIMILARITY,
} from '@/trendData/trendData.constants';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  BaseToolPersistedState,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';

export type ProfileSearchSelectedRegion = { min: number; max: number } | null;

export interface ProfileSearchState extends BaseToolPersistedState {
  inputSignal: ToolItem | null;
  similarity: string | number;
  normalizeAmplitude: boolean;
  normalizeLocation: boolean;
  selectedRegion: ProfileSearchSelectedRegion;
}

export type ProfileSearchDehydratedState = ProfileSearchState;

export interface ProfileSearchActions {
  setSearchName: (name: string) => void;
  setInputSignal: (item: ToolItem | string | null | undefined) => void;
  setSimilarity: (similarity: string | number) => void;
  setNormalizeAmplitude: (normalizeAmplitude: boolean) => void;
  setNormalizeLocation: (normalizeLocation: boolean) => void;
  setSelectedRegion: (selectedRegion: ProfileSearchSelectedRegion) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<ProfileSearchState>;
}

const parameterDefinitions: ParameterDefinitions<'inputSignal'> = {
  inputSignal: { predicate: ['name', 'a'] },
};

const initialState: ProfileSearchState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  inputSignal: null,
  similarity: PROFILE_SEARCH_SIMILARITY,
  normalizeAmplitude: PROFILE_SEARCH_NORMALIZE_AMPLITUDE,
  normalizeLocation: PROFILE_SEARCH_NORMALIZE_LOCATION,
  selectedRegion: null,
});

export const dehydrateProfileSearchState = (state: ProfileSearchState): ProfileSearchDehydratedState => state;

export function getProfileSearchConfigParams(state: ProfileSearchState): ToolConfigParams {
  return getToolConfigParams({
    serializedState: state as unknown as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.PROFILE_SEARCH,
    parameterDefinitionKeys: Object.keys(parameterDefinitions),
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });
}

const profileSearchActionsFactory: ActionsFactory<ProfileSearchState, ProfileSearchActions> = (
  set,
  get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setInputSignal: (item) =>
    set((draft) => {
      draft.inputSignal = sanitizeToolItem(typeof item === 'string' ? { id: item } : item) ?? null;
    }),

  setSimilarity: (similarity) =>
    set((draft) => {
      draft.similarity = similarity;
    }),

  setNormalizeAmplitude: (normalizeAmplitude) =>
    set((draft) => {
      draft.normalizeAmplitude = normalizeAmplitude;
    }),

  setNormalizeLocation: (normalizeLocation) =>
    set((draft) => {
      draft.normalizeLocation = normalizeLocation;
    }),

  setSelectedRegion: (selectedRegion) =>
    set((draft) => {
      draft.selectedRegion = selectedRegion;
    }),

  rehydrateForEdit: (payload) => {
    const currentSelectedRegion = get().selectedRegion;
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      const selectedRegion =
        payload.selectedRegion !== undefined
          ? (payload.selectedRegion as ProfileSearchSelectedRegion)
          : currentSelectedRegion;

      Object.assign(draft, initialState, rehydrationState.config, rehydrationState.selectedParameters, {
        inputSignal: rehydrationState.selectedParameters.inputSignal ?? null,
        originalParameters: rehydrationState.originalParameters,
        selectedRegion,
      });
    });
  },

  reset: resetState,
});

const STORE_NAME = 'ProfileSearch';

const [ProfileSearchStoreProvider, useProfileSearchStoreInstance, useProfileSearchStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  profileSearchActionsFactory,
);

export { ProfileSearchStoreProvider };

export const useProfileSearchId = () => useProfileSearchStoreSelector((state) => state.id);
export const useProfileSearchName = () => useProfileSearchStoreSelector((state) => state.name);
export const useProfileSearchInputSignal = () => useProfileSearchStoreSelector((state) => state.inputSignal);
export const useProfileSearchSimilarity = () => useProfileSearchStoreSelector((state) => state.similarity);
export const useProfileSearchNormalizeAmplitude = () =>
  useProfileSearchStoreSelector((state) => state.normalizeAmplitude);
export const useProfileSearchNormalizeLocation = () =>
  useProfileSearchStoreSelector((state) => state.normalizeLocation);
export const useProfileSearchSelectedRegion = () => useProfileSearchStoreSelector((state) => state.selectedRegion);
export const useProfileSearchActions = () => useProfileSearchStoreInstance().actions;

export function createTestProfileSearchStore(overrides?: Partial<ProfileSearchState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestProfileSearchStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, profileSearchActionsFactory);
}

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateProfileSearchStore() {
  return createStoreInstance<ProfileSearchState, ProfileSearchActions>(initialState, profileSearchActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistProfileSearchStore = () => {
  const register = useRegisterPersistedStore();
  const store = useProfileSearchStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqProfileSearchStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateProfileSearchState,
      }),
    [register, store],
  );
};
